<template>
	<div class="main" v-if="loaded">
		<header class="page-title">
			<h2>{{ provider.name }}</h2>
			<div class="ml-auto">
				<a @click.prevent="updateProviderGenerals" class="btn btn-md btn-success" href="#"><i class="fas fa-save"></i> Save Changes</a>
			</div>
		</header>
		<div class="flex box">
			<div class="card eight-lg">
				<header class="card-header">
					<h4 class="card-title">General</h4>
				</header>
				<div class="card-body">
					<div class="form-group">
						<label for="name">Name</label>
						<input id="name" name="name" type="text" v-model="generalData.name">
						<p class="input-error" v-if="errors.errors.name">{{ errors.errors.name }}</p>
					</div>
					<div class="form-group">
						<label for="slug">Slug</label>
						<div class="input-group">
							<input id="slug" name="slug" :readonly="userRoles.includes('providers.slug') != 1" type="text" v-model="generalData.slug">
							<a v-show="userRoles.includes('providers.slug')" @click.prevent="refreshSlug(generalData.name)" class="btn btn-md btn-default btn-icon-only" href="#" title="Refresh slug"><i class="fas fa-sync-alt"></i></a>
						</div>
						<p class="input-error" v-if="errors.errors.slug">{{ errors.errors.slug }}</p>
					</div>
					<div class="form-group">
						<label for="score">Score</label>
						<input id="score" max="10" min="0" name="score" step=".1" type="number" v-model="generalData.score">
						<p class="input-error" v-if="errors.errors.score">{{ errors.errors.score }}</p>
					</div>
					<div class="form-group">
						<label for="domain_name">Domain Name</label>
						<input id="domain_name" type="text" v-model="generalData.domain_name">
						<p class="input-error" v-if="errors.errors.score">{{ errors.errors.domain_name }}</p>
					</div>
					<hr class="hr-small">
					<div class="flex box">
						<div class="form-group six-lg mb-0">
							<label class="label-checkbox">
								<input name="has_review" type="checkbox" v-model="generalData.has_review">
								<span>Has Review</span>
							</label>
						</div>
						<div class="form-group six-lg mb-0">
							<label class="label-checkbox">
								<input name="is_recommended" type="checkbox" v-model="generalData.is_recommended">
								<span>Recommended by ProPrivacy</span>
							</label>
						</div>
						<div class="form-group six-lg">
							<label class="label-checkbox">
								<input name="out_of_business" type="checkbox" v-model="generalData.out_of_business">
								<span>Out of Business</span>
							</label>
						</div>
					</div>
				</div>
			</div>
			<div class="card four-lg">
				<header class="card-header">
					<h4 class="card-title">Visuals</h4>
				</header>
				<div class="card-body">
					<div class="alert alert-primary alert&#45;&#45;small">
						<div class="alert-content">
							<p>Uploaded logos must be in PNG format, 360 pixels wide, with no whitespace around them.</p>
						</div>
					</div>
					<image-uploader
							:data-image="generalData.logo_url"
							:data-model-id="providerId"
							:data-path="'/providers/'"
							:data-post-url="'/upload/logoImage'"
							:data-title="'Logo'"
					></image-uploader>
				</div>
			</div>
			<div class="card twelve-lg">
				<header class="card-header">
					<h4 class="card-title">Additional</h4>
				</header>
				<div class="card-body">
					<div class="flex box">
						<div class="four-lg">
							<div class="flex center">
								<h4 class="card-subtitle">Pros</h4>
								<a class="btn btn-sm btn-success mb-1 ml-auto" href="#" @click.prevent="addAdditional('pro')">Add</a>
							</div>
							<table>
								<thead>
									<tr>
										<th></th>
										<th>Value</th>
										<th>Action</th>
									</tr>
								</thead>
								<draggable :tag="'tbody'" @change="arrangeAdditionalOrder('pro')" @end="drag=false" @start="drag=true" v-model="additionalData.pros">
									<tr class="draggable" v-for="pro in additionalData.pros">
										<td class="text-center"><i class="fas fa-grip-lines"></i></td>
										<td>{{ pro.translations[defaultLocale].value }}</td>
										<td class="nowrap">
											<a class="btn btn-icon btn-primary" href="#" @click.prevent="editAdditional(pro)"><i class="fas fa-fw fa-pen" title="Edit"></i></a>
											<a class="btn btn-icon btn-danger" href="#" @click.prevent="deleteAdditional(pro)"><i class="fas fa-fw fa-trash" title="Delete"></i></a>
										</td>
									</tr>
								</draggable>
							</table>
						</div>
						<div class="four-lg">
							<div class="flex center">
								<h4 class="card-subtitle">Cons</h4>
								<a class="btn btn-sm btn-success mb-1 ml-auto" href="#" @click.prevent="addAdditional('con')">Add</a>
							</div>
							<table>
								<thead>
									<tr>
										<th></th>
										<th>Value</th>
										<th>Action</th>
									</tr>
								</thead>
								<draggable :tag="'tbody'" @change="arrangeAdditionalOrder('con')" @end="drag=false" @start="drag=true" v-model="additionalData.cons">
									<tr class="draggable" v-for="con in additionalData.cons">
										<td class="text-center"><i class="fas fa-grip-lines"></i></td>
										<td>{{ con.translations[defaultLocale].value }}</td>
										<td class="nowrap">
											<a class="btn btn-icon btn-primary" href="#" @click.prevent="editAdditional(con)"><i class="fas fa-fw fa-pen" title="Edit"></i></a>
											<a class="btn btn-icon btn-danger" href="#" @click.prevent="deleteAdditional(con)"><i class="fas fa-fw fa-trash" title="Delete"></i></a>
										</td>
									</tr>
								</draggable>
							</table>
						</div>
						<div class="four-lg">
							<div class="flex center">
								<h4 class="card-subtitle">Highs</h4>
								<a class="btn btn-sm btn-success mb-1 ml-auto" href="#" @click.prevent="addAdditional('high')">Add</a>
							</div>
							<table>
								<thead>
									<tr>
										<th></th>
										<th>Value</th>
										<th>Action</th>
									</tr>
								</thead>
								<draggable :tag="'tbody'" @change="arrangeAdditionalOrder('high')" @end="drag=false" @start="drag=true" v-model="additionalData.highs">
									<tr class="draggable" v-for="high in additionalData.highs">
										<td class="text-center"><i class="fas fa-grip-lines"></i></td>
										<td>{{ high.translations[defaultLocale].value }}</td>
										<td class="nowrap">
											<a class="btn btn-icon btn-primary" href="#" @click.prevent="editAdditional(high)"><i class="fas fa-fw fa-pen" title="Edit"></i></a>
											<a class="btn btn-icon btn-danger" href="#" @click.prevent="deleteAdditional(high)"><i class="fas fa-fw fa-trash" title="Delete"></i></a>
										</td>
									</tr>
								</draggable>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-bg" v-if="isModalVisible">
			<div class="modal">
				<header class="modal-header">
					<h4 class="modal-title">Additional</h4>
					<a @click.prevent="closeModal" class="modal-close" title="Close modal"><i class="fas fa-times"></i></a>
				</header>
				<section class="modal-body">
					<div class="card">
						<header class="card-header language-list">
							<lang-selector></lang-selector>
						</header>
						<div class="card-body">
							<div class="form-group">
								<label for="value">Value</label>
								<input id="value" name="value" type="text" v-model="selectedAdditional.translations[clickedLang].value" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
								<!--<p class="input-error" v-if="errors.errors[errorNameCurrentLanguage]">{{ errors.errors[errorNameCurrentLanguage] }}</p>-->
							</div>
						</div>
					</div>
				</section>
				<footer class="modal-footer">
					<a @click.prevent="closeModal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Cancel</a>
					<a @click.prevent="saveAdditional" class="btn btn-md btn-success" href="#"><i class="fas fa-check"></i> Save Additional</a>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>
import Errors from "../../Utils/Errors";
import ProviderImageUploader from "../ImageUploader";
import Slug from '../../Utils/Slug'
import {eventBus} from "../../app";
import draggable from 'vuedraggable';

export default {
	components: {ProviderImageUploader, draggable},
	props: {
		providerid: {
			type: Number
		}
	},
	data() {
		return {
			tabName: 'general',
			loaded: false,
			providerId: this.providerid,
			provider: {},
			generalData: {},
			userRoles: this.$currentUserRoles,
			slug: new Slug(),
			slugModified: false,
			additionalData: {},
			selectedAdditional: {},
			countAdditional: 0,

			defaultLocale: this.$defaultLanguage.locale,
			clickedLang: this.$defaultLanguage.locale,

			isModalVisible: false,
			errors: new Errors()
		}
	},
	created() {
		let vm = this;
		vm.fetchDataFromApi();

		// When setActiveStatus method ran, change selected data in form fields
		/* From LangSelector */
		eventBus.$on('setActiveStatus', function (clickedLang) {
			vm.clickedLang = clickedLang;
		});
	},
	methods: {
		fetchDataFromApi: function () {
			let vm = this;
			axios.post('/api/' + vm.$selectedSite.slug + '/providers/fetchtabdata/' + vm.providerId, {
				tab: vm.tabName
			})
					.then(response => {
						if (response.data.hasOwnProperty('generalProviderData')) {
							vm.generalData = response.data.generalProviderData;
							vm.slugModified = response.data.generalProviderData.slug;
							vm.additionalData = response.data.additionals;
							vm.countAdditional = vm.additionalData.highs.length;
						}
						if (response.data.hasOwnProperty('provider')) {
							vm.provider = response.data.provider;
						}
						vm.loaded = true;
					})
					.catch(e => {
						vm.$toastr.error('Api ' + vm.tabName + ' tab load failed', 'Error');
					});
		},
		updateProviderGenerals: function () {
			if (this.slugModified !== this.generalData.slug) {
				this.$dialog.alert('Please create redirect from old url to new url as Slug is now changed!!!');
			} // if
			this.generalData.id = this.provider.id;
			axios.put('/api/' + this.$selectedSite.slug + '/providers/' + this.provider.id, this.generalData)
					.then(response => {
						this.$toastr.success('Provider General saved successfully.');
					})
					.catch(e => {
						this.errors.record(e.response.data);
						this.$toastr.error('Save Provider General failed.', 'Error');
					});
		},
		refreshSlug: function (providerName) {
			this.generalData.slug = this.slug.refreshSlug(providerName.replace(/ /g, ''));
		},
		editAdditional: function (additional) {
			let vm = this;

			vm.selectedAdditional = additional;
			vm.isModalVisible = true;
		},
		saveAdditional: function () {
			let vm = this;

			if(vm.selectedAdditional.hasOwnProperty('id')) {
				axios.put('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/updateProviderAdditional/' + vm.selectedAdditional.id, vm.selectedAdditional)
						.then(function(response) {
							vm.$toastr.success('Provider Additional saved successfully.');
							vm.closeModal();
						})
						.catch(function(e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Save Provider Additional failed.', 'Error');
						});
			} else {
				vm.countAdditional = vm.countAdditional+1;
				vm.selectedAdditional.order = vm.countAdditional;

				axios.post('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/createProviderAdditional', vm.selectedAdditional)
						.then(function(response) {
							vm.selectedAdditional.id = response.data.data.id;
							vm.$toastr.success('Provider Additional created successfully.');
							vm.additionalData[vm.selectedAdditional.type + 's'].push(vm.selectedAdditional);
							vm.closeModal();
						})
						.catch(function(e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Create Provider Additional failed.', 'Error');
						});
			}
		},
		closeModal: function () {
			this.isModalVisible = false;
		},
		arrangeAdditionalOrder: function (type) {
			let vm = this;

			eventBus.$emit('onLoaderStart');
			vm.selectedAdditional = {};

			axios.post('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/arrangeAdditionalOrder', {type: type, data: vm.additionalData[type + 's']})
					.then(function (response) {
						vm.$toastr.success('Additional order modified successfully.');
						eventBus.$emit('onLoaderFinish');
					})
					.catch(function (e) {
						vm.errors.record(e.response.data);
						vm.$toastr.error('Additional order modify failed.', 'Error');
						eventBus.$emit('onLoaderFinish');
					});
		},
		addAdditional: function (type) {
			let vm = this;

			vm.selectedAdditional = {
				translations: {},
				type: null
			};
			for (let i = 0; i < vm.$availableLanguages.length; i++) {
				vm.selectedAdditional.translations[vm.$availableLanguages[i].locale] = {
					value: null
				}
			}
			vm.selectedAdditional.type = type;
			vm.isModalVisible = true;
		},
		deleteAdditional: function (additional) {
			let vm = this;
			let additionalType = additional.type;
			this.$dialog.confirm('Are you sure you want to delete this '+additionalType+'?', {
				loader: true
			})
				.then((dialog) => {
					dialog.close();

					axios.delete('/api/' + vm.$selectedSite.slug + '/providers/' + vm.provider.id + '/providerAdditional/' + additional.id)
						.then(function () {
							vm.$toastr.success(additionalType+' deleted successfully.');
							let i = vm.additionalData[additionalType+'s'].map(item => item.id).indexOf(additional.id);
							vm.additionalData[additionalType+'s'].splice(i, 1);
						})
						.catch(function () {
							vm.$toastr.error('Delete '+additionalType+' failed.', 'Error');
						});
				})
				.catch(() => {
					this.$toastr.error('Delete aborted.');
				});
		},
	}
}
</script>

<style scoped>

</style>
